import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import ImageUploader from './ImageUploader';

const EntityForm = ({ open, title, fields, initialData, onSave, onCancel }) => {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const { token } = useAdminAuth();
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const pendingFiles = useRef({});

  useEffect(() => {
    if (open) {
      pendingFiles.current = {};
      if (initialData) {
        setFormData({ ...initialData });
      } else {
        const defaults = {};
        fields.forEach((f) => {
          if (f.key.includes('.')) {
            const [parent, child] = f.key.split('.');
            if (!defaults[parent]) defaults[parent] = {};
            defaults[parent][child] = f.defaultValue ?? '';
          } else {
            defaults[f.key] = f.defaultValue ?? '';
          }
        });
        setFormData(defaults);
      }
    }
  }, [open, initialData, fields]);

  const getValue = (key) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      return formData[parent]?.[child] ?? '';
    }
    return formData[key] ?? '';
  };

  const setValue = (key, value) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const updatedData = { ...formData };

      // Upload any pending image files first
      for (const [key, file] of Object.entries(pendingFiles.current)) {
        const fieldConfig = fields.find((f) => f.key === key);
        if (!fieldConfig) continue;

        const fd = new FormData();
        fd.append('image', file);
        if (fieldConfig.uploadPath) fd.append('path', fieldConfig.uploadPath);
        const fileNameVal = fieldConfig.fileNameFrom ? getValue(fieldConfig.fileNameFrom) : null;
        if (fileNameVal) fd.append('rename', fileNameVal);

        try {
          const res = await fetch('/admin-upload-image.php', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: fd
          });
          const data = await res.json();
          if (res.ok && data.url) {
            if (key.includes('.')) {
              const [parent, child] = key.split('.');
              updatedData[parent] = { ...updatedData[parent], [child]: data.url };
            } else {
              updatedData[key] = data.url;
            }
          } else {
            alert('Image upload failed: ' + (data.error || 'Unknown error'));
            setSaving(false);
            return;
          }
        } catch {
          // Upload was sent but response lost (e.g. dev server page reload).
          // Build the expected URL and continue - the file was likely saved.
          const ext = file.name.split('.').pop();
          const name = fileNameVal || file.name.replace(/\.[^.]+$/, '');
          const url = '/' + (fieldConfig.uploadPath || 'mech_weapon_images') + '/' + name + '.' + ext;
          if (key.includes('.')) {
            const [parent, child] = key.split('.');
            updatedData[parent] = { ...updatedData[parent], [child]: url };
          } else {
            updatedData[key] = url;
          }
        }
      }

      onSave(updatedData);
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
    dark ? 'bg-[#121212] border-[#333] text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onMouseDown={onCancel} />
      <div className={`relative w-full max-w-lg max-h-[80vh] overflow-y-auto p-6 rounded-xl shadow-xl ${dark ? 'bg-[#1A1A1A] border border-[#333]' : 'bg-white'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${dark ? 'text-white' : 'text-gray-900'}`}>
          {title}
        </h3>
        <div className="space-y-4">
          {fields.map((field) => (
            <div key={field.key}>
              <label className={`block text-sm font-medium mb-1 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                {field.label}
              </label>
              {field.type === 'select' ? (
                <select
                  value={getValue(field.key)}
                  onChange={(e) => setValue(field.key, e.target.value)}
                  className={inputCls}
                  required={field.required}
                >
                  <option value="">Select...</option>
                  {field.options.map((o) => (
                    <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
                      {typeof o === 'string' ? o : o.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'number' ? (
                <input
                  type="number"
                  value={getValue(field.key)}
                  onChange={(e) => setValue(field.key, Number(e.target.value))}
                  className={inputCls}
                  required={field.required}
                  min={field.min}
                  max={field.max}
                />
              ) : field.type === 'image' ? (
                <div className="space-y-2">
                  <ImageUploader
                    currentUrl={getValue(field.key)}
                    onFileSelect={(file) => {
                      pendingFiles.current[field.key] = file;
                    }}
                    acceptTypes={field.acceptTypes}
                  />
                  <input
                    type="text"
                    value={getValue(field.key)}
                    onChange={(e) => {
                      setValue(field.key, e.target.value);
                      delete pendingFiles.current[field.key];
                    }}
                    className={inputCls}
                    placeholder={field.placeholder || 'Or enter URL manually'}
                  />
                </div>
              ) : (
                <input
                  type="text"
                  value={getValue(field.key)}
                  onChange={(e) => setValue(field.key, e.target.value)}
                  className={inputCls}
                  required={field.required}
                  placeholder={field.placeholder}
                />
              )}
            </div>
          ))}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                dark ? 'text-gray-400 hover:bg-[#333]' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${
                dark ? 'bg-[#FF9800] hover:bg-[#F57C00]' : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {saving ? 'Saving...' : (initialData ? 'Save Changes' : 'Add')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntityForm;
